// PermissionRequests.js (Updated with fixes)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AddPermissionRequest from './AddPermissionRequest';
import ViewPermissionRequest from './ViewPermissionRequest';

const PermissionRequests = () => {
    const [permissionRequests, setPermissionRequests] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [salarySettings, setSalarySettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setErrorMessage] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');
    const [sortBy, setSortBy] = useState('latest');
    const [userRole, setUserRole] = useState('');
    const [userPermissions, setUserPermissions] = useState({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_approve: false,
        loaded: false
    });

    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Fetch user role and permissions
    const fetchUserData = useCallback(async () => {
        let currentRole = '';
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_approve: false,
                    loaded: true
                });
                return;
            }

            // Get user role
            const roleResponse = await axios.get(`${API_BASE}/get_users.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (roleResponse.data.success) {
                // Try different possible response formats
                currentRole = roleResponse.data.role ||
                    roleResponse.data.role_name ||
                    roleResponse.data.user_role || '';
                console.log('User role:', currentRole); // Debug log
                setUserRole(currentRole);
            }

            const isAdminRole = currentRole === 'Admin' || currentRole === 'Super Admin';
            console.log('Is Admin/Super Admin:', isAdminRole); // Debug log

            // Get permissions
            const permResponse = await axios.get(`${API_BASE}/get_user_permissions.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (permResponse.data.success && permResponse.data.permissions) {
                const permissionModule = permResponse.data.permissions.find(
                    p => p.module_name === 'PermissionRequests' || p.module_name === 'Permissions'
                );

                if (permissionModule) {
                    console.log('Permission module found:', permissionModule); // Debug log
                    setUserPermissions({
                        can_view: Boolean(permissionModule.can_view),
                        can_add: Boolean(permissionModule.can_add),
                        can_edit: Boolean(permissionModule.can_edit),
                        can_delete: Boolean(permissionModule.can_delete),
                        can_approve: Boolean(permissionModule.can_approve),
                        loaded: true
                    });
                } else {
                    // Default permissions if module not found
                    setUserPermissions({
                        can_view: true,
                        can_add: true,
                        can_edit: isAdminRole,
                        can_delete: isAdminRole,
                        can_approve: isAdminRole,
                        loaded: true
                    });
                }
            } else {
                // Default permissions if no permission data
                setUserPermissions({
                    can_view: true,
                    can_add: true,
                    can_edit: isAdminRole,
                    can_delete: isAdminRole,
                    can_approve: isAdminRole,
                    loaded: true
                });
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            const isAdminRole = currentRole === 'Admin' || currentRole === 'Super Admin';
            setUserPermissions({
                can_view: true,
                can_add: true,
                can_edit: isAdminRole,
                can_delete: isAdminRole,
                can_approve: isAdminRole,
                loaded: true
            });
        }
    }, [API_BASE]);

    // Fetch permission requests
    const fetchPermissionRequests = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setErrorMessage('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}prequest/get_permission_requests.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setPermissionRequests(response.data.permission_requests || []);
                setErrorMessage('');
            } else {
                setPermissionRequests([]);
                const errorMsg = response.data?.error || 'Failed to fetch permission requests';
                setErrorMessage(errorMsg);
            }
        } catch (error) {
            setPermissionRequests([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch permission requests';
            setErrorMessage(errorMsg);
            console.error('Error fetching permission requests:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    // Fetch employees
    const fetchEmployees = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.get(`${API_BASE}/get_employees.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setEmployees(response.data.employees || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    }, [API_BASE]);

    // Fetch salary settings
    const fetchSalarySettings = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.get(`${API_BASE}salarysettings/get_salary_settings.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setSalarySettings(response.data.salary_settings || []);
            }
        } catch (error) {
            console.error('Error fetching salary settings:', error);
        }
    }, [API_BASE]);

    useEffect(() => {
        const initializeData = async () => {
            try {
                await fetchUserData();
                await Promise.all([
                    fetchPermissionRequests(),
                    fetchEmployees(),
                    fetchSalarySettings()
                ]);
            } catch (error) {
                console.error('Initialization error:', error);
                setErrorMessage(`Initialization failed: ${error.message}`);
            }
        };

        initializeData();
    }, [fetchPermissionRequests, fetchEmployees, fetchSalarySettings, fetchUserData]);

    // Permission check functions
    const canView = () => userPermissions.can_view;

    const canAdd = () => userPermissions.can_add;

    const canApprove = () => {
        return true;
    };

    const handleAdd = () => {
        if (!canAdd()) {
            alert('You do not have permission to add permission requests');
            return;
        }
        setShowAddModal(true);
    };

    const handleView = (request) => {
        setSelectedRequest(request);
        setShowViewModal(true);
    };

    const getEmployeeSalarySettings = (empId) => {
        return salarySettings.find(setting => setting.emp_id === empId) || {};
    };

    const getPermissionLimitsForEmployee = (empId) => {
        const settings = getEmployeeSalarySettings(empId);
        return {
            permission_per_month: settings.permission_per_month || 0,
            permission_hour: settings.permission_hour || 0,
            weeklyoff_1: settings.weeklyoff_1 || 'Sunday',
            weeklyoff_2: settings.weeklyoff_2 || null
        };
    };

    const getUsedPermissionsThisMonth = (empId) => {
        const currentDate = new Date();
        const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

        return permissionRequests.filter(request =>
            request.emp_id === empId &&
            request.status !== 'Cancelled' &&
            request.status !== 'Rejected' &&
            new Date(request.permission_date) >= monthStart &&
            new Date(request.permission_date) <= monthEnd
        ).length;
    };

    const handleApprove = async (permissionId, action) => {
        const confirmMessage = action === 'approve'
            ? 'Are you sure you want to approve this permission request? This will update attendance records.'
            : 'Are you sure you want to reject this permission request?';

        if (!window.confirm(confirmMessage)) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');

            // Use the new permission_approve.php endpoint
            const response = await axios.post(`${API_BASE}prequest/permission_approve.php`,
                {
                    permission_id: permissionId,
                    status: action === 'approve' ? 'Approved' : 'Rejected'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                await fetchPermissionRequests();

                // Show detailed success message for approval
                if (action === 'approve' && response.data.attendance_updated) {
                    alert('Permission request approved successfully. Attendance has been updated.');
                } else {
                    alert(`Permission request ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
                }
            } else {
                setErrorMessage(response.data.error || `Failed to ${action} permission request`);
                alert(response.data.error || `Failed to ${action} permission request`);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || `Failed to ${action} permission request`;
            setErrorMessage(errorMsg);
            alert(errorMsg);
            console.error(`Error ${action}ing permission request:`, error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (permissionId) => {
        if (!window.confirm('Are you sure you want to cancel this permission request?')) return;

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}prequest/update_permission_status.php`,
                {
                    permission_id: permissionId,
                    status: 'Cancelled'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                await fetchPermissionRequests();
                alert('Permission request cancelled successfully');
            } else {
                setErrorMessage(response.data.error || 'Failed to cancel permission request');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Failed to cancel permission request';
            setErrorMessage(errorMsg);
            console.error('Error cancelling permission request:', error);
        }
    };

    const getEmployeeName = (empId) => {
        const employee = employees.find(emp => emp.emp_id === empId);
        return employee ? `${employee.first_name} ${employee.last_name}` : `Employee ${empId}`;
    };

    const getEmployeeCode = (empId) => {
        const employee = employees.find(emp => emp.emp_id === empId);
        return employee ? employee.emp_code : '';
    };

    const calculateHours = (fromTime, toTime) => {
        if (!fromTime || !toTime) return 0;
        const from = new Date(`1970-01-01T${fromTime}`);
        const to = new Date(`1970-01-01T${toTime}`);
        const diffMs = to - from;
        return (diffMs / (1000 * 60 * 60)).toFixed(2);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const filteredRequests = permissionRequests.filter(request => {
        const employee = employees.find(emp => emp.emp_id === request.emp_id);
        const matchesSearch = employee ?
            employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.emp_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.reason?.toLowerCase().includes(searchTerm.toLowerCase()) :
            request.emp_id?.toString().includes(searchTerm.toLowerCase()) ||
            request.reason?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = filterStatus === 'All' || request.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    const sortedRequests = [...filteredRequests].sort((a, b) => {
        switch (sortBy) {
            case 'latest':
                return new Date(b.created_at) - new Date(a.created_at);
            case 'oldest':
                return new Date(a.created_at) - new Date(b.created_at);
            case 'date':
                return new Date(b.permission_date) - new Date(a.permission_date);
            case 'employee':
                return getEmployeeName(a.emp_id).localeCompare(getEmployeeName(b.emp_id));
            case 'status':
                return a.status.localeCompare(b.status);
            default:
                return b.permission_id - a.permission_id;
        }
    });

    // Debug log to check permissions and role
    console.log('Current state:', {
        userRole,
        userPermissions,
        canApprove: canApprove(),
        pendingRequests: permissionRequests.filter(r => r.status === 'Pending').length
    });

    if ((loading && permissionRequests.length === 0) || !userPermissions.loaded) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading permission requests...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!canView()) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                                <h3 className="mt-3 text-warning">Access Denied</h3>
                                <p className="text-muted">You do not have permission to view permission requests.</p>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    <i className="bi bi-speedometer2 me-2"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3" role="group">
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/dashboard')}
                                                title="Go to Dashboard"
                                            >
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/Menu')}
                                                title="Go to Menu"
                                            >
                                                <i className="bi bi-menu-button"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-clock-history me-2 text-info"></i>
                                                Permission Requests
                                                <span className="badge bg-info ms-2">
                                                    {salarySettings.filter(s => s.permission_per_month > 0 || s.permission_hour > 0).length} Employees with Limits
                                                </span>
                                            </h2>
                                            <p className="text-muted mb-0">
                                                Manage short-time permissions based on salary settings
                                            </p>

                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={() => navigate('/salary-settings')}
                                            title="Configure Salary Settings"
                                        >
                                            <i className="bi bi-gear me-2"></i>
                                            Salary Settings
                                        </button>
                                        {canAdd() && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleAdd}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                New Permission Request
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Settings Summary */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-3 mb-2 mb-md-0">
                                    <div className="d-flex flex-column">
                                        <span className="fw-bold text-primary fs-4">
                                            {salarySettings.filter(s => s.permission_per_month > 0).length}
                                        </span>
                                        <small className="text-muted">Employees with Monthly Limits</small>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-2 mb-md-0">
                                    <div className="d-flex flex-column">
                                        <span className="fw-bold text-success fs-4">
                                            {salarySettings.filter(s => s.permission_hour > 0).length}
                                        </span>
                                        <small className="text-muted">Employees with Hour Limits</small>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-2 mb-md-0">
                                    <div className="d-flex flex-column">
                                        <span className="fw-bold text-info fs-4">
                                            {salarySettings.filter(s => s.permission_per_month === 0 && s.permission_hour === 0).length}
                                        </span>
                                        <small className="text-muted">Unlimited Permissions</small>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-2 mb-md-0">
                                    <div className="d-flex flex-column">
                                        <span className="fw-bold text-warning fs-4">
                                            {employees.filter(emp =>
                                                !salarySettings.find(s => s.emp_id === emp.emp_id)
                                            ).length}
                                        </span>
                                        <small className="text-muted">Employees without Settings</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row g-3 align-items-center">
                                <div className="col-md-4">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search by employee name, code, or reason..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                    >
                                        <option value="All">All Status</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Rejected">Rejected</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="latest">Newest First</option>
                                        <option value="oldest">Oldest First</option>
                                        <option value="date">Sort by Date</option>
                                        <option value="employee">Sort by Employee</option>
                                        <option value="status">Sort by Status</option>
                                    </select>
                                </div>
                                <div className="col-md-2 text-end">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                            fetchPermissionRequests();
                                            fetchSalarySettings();
                                        }}
                                        title="Refresh"
                                    >
                                        <i className="bi bi-arrow-clockwise"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Permission Requests Table */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body p-0">
                            {sortedRequests.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-clock-history display-1 text-muted"></i>
                                    <h4 className="mt-3 text-muted">No permission requests found</h4>
                                    <p className="text-muted">
                                        {permissionRequests.length === 0
                                            ? "No permission requests available. Create your first request."
                                            : "No requests match your search criteria."
                                        }
                                    </p>
                                    {permissionRequests.length === 0 && canAdd() && (
                                        <button
                                            className="btn btn-primary mt-2"
                                            onClick={handleAdd}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Create Permission Request
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4">Employee & Limits</th>
                                                <th>Date & Time</th>
                                                <th>Duration</th>
                                                <th>Reason</th>
                                                <th>Status</th>
                                                <th>Submitted</th>
                                                <th className="text-end pe-4">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedRequests.map((request) => {
                                                const limits = getPermissionLimitsForEmployee(request.emp_id);
                                                const usedThisMonth = getUsedPermissionsThisMonth(request.emp_id);
                                                const isAtLimit = limits.permission_per_month > 0 && usedThisMonth >= limits.permission_per_month;
                                                const showApproveButtons = request.status === 'Pending';

                                                return (
                                                    <tr key={request.permission_id} className={isAtLimit && request.status === 'Pending' ? 'table-warning' : ''}>
                                                        <td className="ps-4">
                                                            <div className="d-flex align-items-center">
                                                                <div className="employee-icon bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                                                    style={{ width: '40px', height: '40px' }}
                                                                >
                                                                    <i className="bi bi-person"></i>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-semibold">
                                                                        {getEmployeeName(request.emp_id)}
                                                                    </div>
                                                                    <small className="text-muted d-block">
                                                                        ID: {request.emp_id} | Code: {getEmployeeCode(request.emp_id)}
                                                                    </small>
                                                                    {limits.permission_per_month > 0 && (
                                                                        <small className={`d-block ${isAtLimit ? 'text-danger' : 'text-success'}`}>
                                                                            <i className="bi bi-calendar-check me-1"></i>
                                                                            {usedThisMonth}/{limits.permission_per_month} per month
                                                                        </small>
                                                                    )}
                                                                    {limits.permission_hour > 0 && (
                                                                        <small className="text-info d-block">
                                                                            <i className="bi bi-clock me-1"></i>
                                                                            Max {limits.permission_hour} hours per permission
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <div className="fw-semibold">
                                                                    {formatDate(request.permission_date)}
                                                                    {request.permission_date && (
                                                                        <span className="badge bg-secondary ms-1">
                                                                            {new Date(request.permission_date).toLocaleDateString('en-US', { weekday: 'short' })}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {request.from_time} - {request.to_time}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${limits.permission_hour > 0 && request.total_hours > limits.permission_hour
                                                                ? 'bg-danger'
                                                                : 'bg-primary'
                                                                }`}>
                                                                {request.total_hours || calculateHours(request.from_time, request.to_time)} hours
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={request.reason}>
                                                                {request.reason || 'No reason provided'}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${request.status === 'Pending' ? 'bg-warning' :
                                                                request.status === 'Approved' ? 'bg-success' :
                                                                    request.status === 'Rejected' ? 'bg-danger' :
                                                                        'bg-secondary'
                                                                }`}>
                                                                {request.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <small className="text-muted d-block">
                                                                {formatDate(request.created_at)}
                                                            </small>
                                                            <small className="text-muted">
                                                                {new Date(request.created_at).toLocaleTimeString()}
                                                            </small>
                                                        </td>
                                                        <td className="text-end pe-4">
                                                            <div className="btn-group" role="group">
                                                                <button
                                                                    className="btn btn-outline-info btn-sm"
                                                                    onClick={() => handleView(request)}
                                                                    title="View Details"
                                                                >
                                                                    <i className="bi bi-eye"></i>
                                                                </button>

                                                                {showApproveButtons && (
                                                                    <>
                                                                        <button
                                                                            className="btn btn-outline-success btn-sm"
                                                                            onClick={() => handleApprove(request.permission_id, 'approve')}
                                                                            title="Approve (Updates Attendance)"
                                                                            disabled={isAtLimit}
                                                                        >
                                                                            <i className="bi bi-check-lg"></i> Approve
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-outline-danger btn-sm"
                                                                            onClick={() => handleApprove(request.permission_id, 'reject')}
                                                                            title="Reject"
                                                                        >
                                                                            <i className="bi bi-x-lg"></i> Reject
                                                                        </button>
                                                                    </>
                                                                )}

                                                                {request.status === 'Pending' && (
                                                                    <button
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => handleCancel(request.permission_id)}
                                                                        title="Cancel"
                                                                    >
                                                                        <i className="bi bi-slash-circle"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {isAtLimit && request.status === 'Pending' && showApproveButtons && (
                                                                <small className="text-danger d-block mt-1">
                                                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                                                    Monthly limit reached - cannot approve
                                                                </small>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {showAddModal && canAdd() && (
                <AddPermissionRequest
                    show={showAddModal}
                    onHide={() => setShowAddModal(false)}
                    employees={employees}
                    salarySettings={salarySettings}
                    onPermissionAdded={fetchPermissionRequests}
                />
            )}

            {showViewModal && selectedRequest && (
                <ViewPermissionRequest
                    show={showViewModal}
                    onHide={() => setShowViewModal(false)}
                    request={selectedRequest}
                    employeeName={getEmployeeName(selectedRequest.emp_id)}
                    employeeCode={getEmployeeCode(selectedRequest.emp_id)}
                    salarySettings={getEmployeeSalarySettings(selectedRequest.emp_id)}
                    canApprove={canApprove()}
                    userRole={userRole}
                    onStatusChange={fetchPermissionRequests}
                />
            )}
        </div>
    );
};

export default PermissionRequests;
